/**
 * TOTP Setup Component
 * Allows users to enable/disable two-factor authentication
 */

import { useState } from 'react';
import { Shield, ShieldCheck, ShieldOff, Copy, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { TotpInput } from './TotpInput';
import { cn } from '@/lib/utils';

interface TotpSetupResponse {
  success: boolean;
  data?: {
    secret: string;
    qr_uri: string;
    backup_codes: string[];
  };
  error?: {
    code: string;
    message: string;
  };
}

interface TotpEnableResponse {
  success: boolean;
  data?: {
    message: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

interface TotpDisableResponse {
  success: boolean;
  data?: {
    message: string;
  };
  error?: {
    code: string;
    message: string;
  };
}

interface TotpSetupProps {
  isEnabled: boolean;
  onStatusChange?: (enabled: boolean) => void;
}

type SetupStep = 'idle' | 'loading' | 'show-qr' | 'verify' | 'show-backup' | 'disable-confirm';

export function TotpSetup({ isEnabled, onStatusChange }: TotpSetupProps) {
  const token = useAuthStore((state) => state.token);

  const [step, setStep] = useState<SetupStep>('idle');
  const [secret, setSecret] = useState<string | null>(null);
  const [qrUri, setQrUri] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [verifyError, setVerifyError] = useState(false);

  const handleStartSetup = async () => {
    setStep('loading');
    setError(null);

    try {
      const response = await fetch('/api/v1/auth/totp/setup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data: TotpSetupResponse = await response.json();

      if (!data.success || !data.data) {
        throw new Error(data.error?.message || 'Failed to start setup');
      }

      setSecret(data.data.secret);
      setQrUri(data.data.qr_uri);
      setBackupCodes(data.data.backup_codes);
      setStep('show-qr');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed');
      setStep('idle');
    }
  };

  const handleVerifyCode = async (code: string) => {
    setError(null);
    setVerifyError(false);

    try {
      const response = await fetch('/api/v1/auth/totp/enable', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ code }),
      });

      const data: TotpEnableResponse = await response.json();

      if (!data.success) {
        setVerifyError(true);
        throw new Error(data.error?.message || 'Invalid code');
      }

      setStep('show-backup');
      onStatusChange?.(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setVerifyError(true);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword) {
      setError('Password is required');
      return;
    }

    setStep('loading');
    setError(null);

    try {
      const response = await fetch('/api/v1/auth/totp/disable', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password: disablePassword }),
      });

      const data: TotpDisableResponse = await response.json();

      if (!data.success) {
        throw new Error(data.error?.message || 'Failed to disable');
      }

      setStep('idle');
      setDisablePassword('');
      onStatusChange?.(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable');
      setStep('disable-confirm');
    }
  };

  const copyToClipboard = async (text: string, type: 'secret' | 'codes') => {
    await navigator.clipboard.writeText(text);
    if (type === 'secret') {
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } else {
      setCopiedCodes(true);
      setTimeout(() => setCopiedCodes(false), 2000);
    }
  };

  const handleFinish = () => {
    setStep('idle');
    setSecret(null);
    setQrUri(null);
    setBackupCodes([]);
  };

  // Loading state
  if (step === 'loading') {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show QR code step
  if (step === 'show-qr' && qrUri && secret) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Scan QR Code</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Scan this QR code with your authenticator app
          </p>
        </div>

        {/* QR Code */}
        <div className="flex justify-center">
          <div className="bg-white p-4 rounded-lg">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUri)}`}
              alt="TOTP QR Code"
              className="w-48 h-48"
            />
          </div>
        </div>

        {/* Manual entry secret */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground text-center">
            Or enter this code manually:
          </p>
          <div className="flex items-center gap-2 justify-center">
            <code className="bg-muted px-3 py-2 rounded font-mono text-sm break-all">
              {secret}
            </code>
            <button
              onClick={() => copyToClipboard(secret, 'secret')}
              className="p-2 hover:bg-muted rounded transition-colors"
            >
              {copiedSecret ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <button
          onClick={() => setStep('verify')}
          className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium"
        >
          Next: Verify Code
        </button>
      </div>
    );
  }

  // Verify code step
  if (step === 'verify') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Verify Setup</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Enter the 6-digit code from your authenticator app
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <TotpInput
          onComplete={handleVerifyCode}
          error={verifyError}
          autoFocus
        />

        <button
          onClick={() => setStep('show-qr')}
          className="w-full text-sm text-muted-foreground hover:text-primary"
        >
          Back to QR code
        </button>
      </div>
    );
  }

  // Show backup codes step
  if (step === 'show-backup' && backupCodes.length > 0) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
            <ShieldCheck className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold">Two-Factor Authentication Enabled</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Save these backup codes in a safe place
          </p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Important</p>
              <p>Each backup code can only be used once. Store them securely.</p>
            </div>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((code, index) => (
              <code key={index} className="font-mono text-sm p-2 bg-background rounded text-center">
                {code}
              </code>
            ))}
          </div>
          <button
            onClick={() => copyToClipboard(backupCodes.join('\n'), 'codes')}
            className="mt-3 w-full flex items-center justify-center gap-2 py-2 text-sm border rounded hover:bg-background transition-colors"
          >
            {copiedCodes ? (
              <>
                <Check className="h-4 w-4 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                Copy all codes
              </>
            )}
          </button>
        </div>

        <button
          onClick={handleFinish}
          className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium"
        >
          Done
        </button>
      </div>
    );
  }

  // Disable confirmation step
  if (step === 'disable-confirm') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-3">
            <ShieldOff className="h-6 w-6 text-destructive" />
          </div>
          <h3 className="text-lg font-semibold">Disable Two-Factor Authentication</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Enter your password to confirm
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        <div>
          <label htmlFor="disable-password" className="block text-sm font-medium mb-2">
            Password
          </label>
          <input
            id="disable-password"
            type="password"
            value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)}
            className="w-full rounded-md border bg-background px-4 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Enter your password"
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => {
              setStep('idle');
              setDisablePassword('');
              setError(null);
            }}
            className="flex-1 py-2 px-4 border rounded-lg hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDisable}
            className="flex-1 py-2 px-4 bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90 font-medium"
          >
            Disable
          </button>
        </div>
      </div>
    );
  }

  // Idle state - show enable/disable button
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className={cn(
          'w-10 h-10 rounded-full flex items-center justify-center',
          isEnabled ? 'bg-green-100' : 'bg-muted'
        )}>
          {isEnabled ? (
            <ShieldCheck className="h-5 w-5 text-green-600" />
          ) : (
            <Shield className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div>
          <h4 className="font-medium">Two-Factor Authentication</h4>
          <p className="text-sm text-muted-foreground">
            {isEnabled
              ? 'Your account is protected with 2FA'
              : 'Add an extra layer of security to your account'}
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {isEnabled ? (
        <button
          onClick={() => setStep('disable-confirm')}
          className="w-full py-2 px-4 border border-destructive text-destructive rounded-lg hover:bg-destructive/10 transition-colors"
        >
          Disable Two-Factor Authentication
        </button>
      ) : (
        <button
          onClick={handleStartSetup}
          className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium"
        >
          Enable Two-Factor Authentication
        </button>
      )}
    </div>
  );
}
